import { action, computed, makeObservable } from 'mobx'

import { SitePermitStatus } from '~/client/graph'
import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { LOCATION_SEPARATOR } from '~/client/src/shared/utils/usefulStrings'

import { UNASSIGNED_FILTER_OPTION } from '../components/Deliveries/DeliveriesView.store'
import { TagType } from '../enums/TagType'
import { getFormStatusDisplayName } from '../localization/enumDisplayTexts'
import LocationBase from '../models/LocationObjects/LocationBase'
import { UNKNOWN_COMPANY_NAME, UNKNOWN_USER_NAME } from '../models/User'
import { UNASSIGNED } from '../utils/ZoneLevelLocationConstants'
import BaseLogisticsFilterStore, {
  ISourceMap,
} from './BaseLogisticsFilter.store'
import ProjectsStore from './domain/Projects.store'
import SharedSitePermitStore from './domain/SharedSitePermit.store'
import WorkflowCategoriesStore from './domain/WorkflowCategories.store'
import UIFilterInfo from './substates/UIFilterInfo'
import BaseLogisticsStore from './ui/BaseLogistics.store'

export default abstract class LogisticsFilterStore {
  public constructor(
    protected readonly eventsStore: EventsStore,
    protected readonly logisticsStore: BaseLogisticsStore,
    protected readonly onShowChanged: (
      isShown: boolean,
      filterType: string,
    ) => void,
    protected readonly locationAttributesStore: LocationAttributesStore,
    protected readonly permitTypesStore: WorkflowTypesStore,
    protected readonly tagsStore: TagsStore,
    protected readonly formCategoriesStore: WorkflowCategoriesStore,
    protected readonly projectsStore: ProjectsStore,
    protected readonly sharedSitePermitStore: SharedSitePermitStore,
  ) {
    makeObservable(this)
  }

  protected abstract get availableInstances(): any[]

  public abstract get filterStoresByTypeMap(): {
    [filterType: string]: BaseLogisticsFilterStore
  }

  protected abstract get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  }

  @action.bound
  public resetAllFilters() {
    this.eventsStore.dispatch(RESET_ALL_FILTERS)
  }

  protected abstract getDefaultSourceMapByType(type: LogisticsFilterType)

  protected getOptionName = (option: string, filterType: string): string => {
    switch (filterType) {
      case LogisticsFilterType.Location:
      case LogisticsFilterType.Equipment:
        return this.getLocationOptionName(option)
      case LogisticsFilterType.Company:
        return this.getCompanyOptionName(option)
      case LogisticsFilterType.Type:
        return this.getTypeOptionName(option)
      case LogisticsFilterType.ResponsibleContact:
        return this.getUserOptionName(option)
      case LogisticsFilterType.Status:
        return this.getStatusOptionName(option)
      case LogisticsFilterType.Category:
        return this.getCategoryOptionName(option)
      case LogisticsFilterType.Project:
        return this.getProjectOptionName(option)
      default:
        return option
    }
  }

  protected getLocationOptionName = (option: string): string => {
    const [, attributeId] = option.split(LOCATION_SEPARATOR)

    // By id: the kind half names the store the location sat in when the key was written,
    // which a re-kind changes and a project-added kind has none of (STX1-597)
    return (
      this.tagsStore.getLocationByIdWithDeleted(attributeId)?.name || option
    )
  }

  protected getTypeOptionName = (option: string): string => {
    const permitType = this.permitTypesStore.getLastUpdatedTypeByType(option)
    return this.logisticsStore.getFormNameToDisplay(permitType) || option
  }

  protected getUserOptionName = (option: string): string => {
    return (
      this.tagsStore.getUserTagInstanceById(option)?.name || UNKNOWN_USER_NAME
    )
  }

  protected getStatusOptionName = (option: string): string => {
    if (Object.values(SitePermitStatus).includes(option as SitePermitStatus)) {
      return getFormStatusDisplayName(option as SitePermitStatus)
    }
    return UNASSIGNED
  }

  protected getCompanyOptionName = (option: string): string => {
    return (
      this.tagsStore.getTag(TagType.Company, option)?.name ||
      UNKNOWN_COMPANY_NAME
    )
  }

  protected getCategoryOptionName = (option: string): string => {
    return this.formCategoriesStore.getNameById(option)
  }

  protected getProjectOptionName = (option: string): string => {
    return (
      this.projectsStore.getProjectById(option)?.name ||
      this.sharedSitePermitStore.projectFromIdToCodeMap[option]
    )
  }

  @action.bound
  public syncFilters() {
    Object.keys(this.fieldsMap).forEach(filterType => {
      const filter = this.fieldsMap[filterType]
      const sourceMap = this.sourceMapByFilterTypeMap[filterType]
      this.updateCommonFilterState(filter, sourceMap)
    })
  }

  @action.bound
  private updateCommonFilterState(filter: UIFilterInfo, sourceMap: ISourceMap) {
    let appliedFilterOptions = []

    filter.initialFilterOptions.forEach((_, optionKey) => {
      const deliveriesIds = sourceMap[optionKey]

      filter.selectedFilterOptions.set(optionKey, deliveriesIds)
      filter.initialFilterOptions.set(optionKey, deliveriesIds)

      appliedFilterOptions = appliedFilterOptions.concat(deliveriesIds)
    })

    filter.appliedFilterOptions = appliedFilterOptions
  }

  protected abstract fieldsMap()

  protected getLocationValues = (locations: LocationBase[]): string[] => {
    return locations?.length
      ? locations.map(l => `${l.type}${LOCATION_SEPARATOR}${l.id}`)
      : [UNASSIGNED_FILTER_OPTION]
  }

  @computed
  public get selectedOptionsCount(): number {
    return Object.values(this.filterStoresByTypeMap)
      .map(value => value.selectedOptions.size)
      .reduce((a, c) => a + c)
  }
}
